import { useBubbleTextMenuStates } from '@/components/tiptap/hooks/useBubbleTextMenuStates';
import { EditorAnalyticsProvider } from '@/components/tiptap/shared/editor-analytics-provider';
import { TextMenuWrapper } from '@/components/tiptap/shared/text-menu-base';
import {
  BoldButton,
  BulletListButton,
  ConvertToSectionButton,
  CopyButton,
  FindReplaceButton,
  ItalicButton,
  OrderedListButton,
  SnippetButton,
  UnderlineButton,
} from '@/components/tiptap/shared/text-menu-buttons';
import { Toolbar } from '@/components/tiptap/ui/Toolbar';
import { isTextSelected } from '@/components/tiptap/utils/isTextSelected';
import { useEmphasisDisabled, useEmphasisDisabledTooltip } from '@/features/copySettings';
import { isMac } from '@/hooks/platform';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { toast } from '@heidi/design-system/toast';
import { EditorType } from '@heidi/editor/core';
import { type Editor } from '@tiptap/react';
import { Code, Link, Strikethrough } from 'lucide-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Key } from 'ts-key-enum';
import { EditorTypeContext } from '../editor-type';
import { isLinkMenuEditing } from '../link-menu/use-link-menu';
import { HeadingPicker } from './heading-picker';
import { LinkEditorPopover } from './link-editor-popover';
import { selectMarkdownNativeFormatStates } from './text-menu-format-state';
import { useMarkdownNativeTextMenuCommands } from './use-markdown-native-text-menu-commands';

// Matches the toast display duration (and the emphasis-shortcut gate).
const NESTED_MARKS_TOAST_THROTTLE_MS = 5000;

/**
 * Floating text/bubble menu for the markdown-native editor — a twin of the
 * vMarkdown text menu, composed from the shared building blocks (no vMarkdown
 * imports). Bold/italic reuse the shared button components; the marks/nodes
 * without an existing building block inline `Toolbar.Button`, as the sibling
 * `TableMenu` twin does. Mounted inside `MarkdownNativeEditor`, so it is gated
 * behind the markdown-native flag transitively.
 */
export const MarkdownNativeTextMenu = ({
  editor,
  enableFindReplace = true,
  enableSnippet = true,
}: {
  editor: Editor;
  /**
   * Note-scoped affordances, on by default. The template editor hides both —
   * find-and-replace targets the note surface, and a snippet cut from template
   * scaffolding is not a snippet. Mirrors the same two props on the vMarkdown
   * `TextMenu`, which the legacy template surface already passes `false` for.
   */
  enableFindReplace?: boolean;
  enableSnippet?: boolean;
}) => {
  const intl = useIntl();
  const [linkEditorOpen, setLinkEditorOpen] = useState(false);
  const hostEditorType = useContext(EditorTypeContext);
  const emphasisDisabled = useEmphasisDisabled(hostEditorType === EditorType.NOTE);
  const emphasisDisabledTooltip = useEmphasisDisabledTooltip();
  const {
    isHeading,
    headingLevel,
    canShowHeadingPicker,
    isBold,
    isItalic,
    isUnderline,
    isStrike,
    isCode,
    isBulletList,
    isOrderedList,
    isLink,
    canConvertToSection,
    shouldShow,
  } = useBubbleTextMenuStates({
    editor,
    selector: '',
    selectFormatStates: selectMarkdownNativeFormatStates,
  });

  // TipTap's `code` mark excludes every other mark (`excludes: '_'`), so bold /
  // italic / strike / link cannot apply to a whole-code selection. Disable the
  // controls; toast on shortcut (and blocked link-open). Native `disabled`
  // swallows click — the tooltip covers that path.
  const nestedMarksBlocked = isCode;
  const nestedMarksBlockedTooltip = intl.formatMessage({
    defaultMessage: "Can't apply this style to inline code",
    id: '3iuWBikQy+',
  });
  const lastNestedMarksToastAt = useRef<number | null>(null);
  const notifyNestedMarksBlocked = useCallback(() => {
    const now = Date.now();
    if (
      lastNestedMarksToastAt.current !== null &&
      now - lastNestedMarksToastAt.current < NESTED_MARKS_TOAST_THROTTLE_MS
    ) {
      return;
    }
    lastNestedMarksToastAt.current = now;
    toast({ tone: 'warning', title: nestedMarksBlockedTooltip });
  }, [nestedMarksBlockedTooltip]);
  const emphasisOrCodeDisabled = emphasisDisabled || nestedMarksBlocked;
  const emphasisOrCodeTooltip = nestedMarksBlocked
    ? nestedMarksBlockedTooltip
    : emphasisDisabledTooltip;

  const onBold = useCallback(() => editor.chain().focus().toggleBold().run(), [editor]);
  const onItalic = useCallback(
    () => editor.chain().focus().toggleItalic().run(),
    [editor]
  );
  const onUnderline = useCallback(
    () => editor.chain().focus().toggleUnderline().run(),
    [editor]
  );
  const onStrike = useCallback(
    () => editor.chain().focus().toggleStrike().run(),
    [editor]
  );
  const onInlineCode = useCallback(
    () => editor.chain().focus().toggleCode().run(),
    [editor]
  );
  const onBulletList = useCallback(
    () => editor.chain().focus().toggleBulletList().run(),
    [editor]
  );
  const onOrderedList = useCallback(
    () => editor.chain().focus().toggleOrderedList().run(),
    [editor]
  );
  const { onCopy, onFindReplace, onCreateSnippet, onConvertToSection } =
    useMarkdownNativeTextMenuCommands(editor);
  // Sections and lists are always registered in the markdown-native kit; the
  // runtime checks keep the buttons correct if that ever becomes conditional.
  const sectionsEnabled = editor.extensionManager.extensions.some(
    (ext) => ext.name === 'sectionNode'
  );
  const listsEnabled = editor.extensionManager.extensions.some(
    (ext) => ext.name === 'bulletList'
  );

  // Capture-phase so we beat TipTap's own Mod-B / Mod-I / Mod-Shift-S bindings.
  // Mod-K opens the link editor whenever text is selected — `@tiptap/extension-link`
  // binds no shortcut. Same `isCode` predicate as the disabled buttons.
  useEffect(() => {
    const dom = editor.view.dom;
    const onKeyDown = (event: KeyboardEvent) => {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;
      const key = event.key.toLowerCase();
      const nestedMarkShortcut =
        key === 'b' || key === 'i' || key === 'k' || (key === 's' && event.shiftKey);
      if (nestedMarkShortcut && nestedMarksBlocked) {
        event.preventDefault();
        event.stopPropagation();
        if (!event.repeat) {
          notifyNestedMarksBlocked();
        }
        return;
      }
      if (key === 'k' && isTextSelected({ editor })) {
        event.preventDefault();
        setLinkEditorOpen(true);
      }
    };
    dom.addEventListener('keydown', onKeyDown, true);
    return () => dom.removeEventListener('keydown', onKeyDown, true);
  }, [editor, nestedMarksBlocked, notifyNestedMarksBlocked]);

  return (
    <EditorAnalyticsProvider editorType="markdown_native">
      <TextMenuWrapper
        editor={editor}
        shouldShow={(props) => !isLinkMenuEditing(editor) && shouldShow(props)}
      >
        {/* Block-style picker: only when the whole selection is safe to convert
            (no section title, no list — those carry their own structure). */}
        {canShowHeadingPicker && (
          <HeadingPicker editor={editor} activeLevel={headingLevel} />
        )}
        {!isHeading && (
          <BoldButton
            onClick={onBold}
            active={isBold}
            disabled={emphasisOrCodeDisabled}
            disabledTooltip={emphasisOrCodeTooltip}
          />
        )}
        <ItalicButton
          onClick={onItalic}
          active={isItalic}
          disabled={emphasisOrCodeDisabled}
          disabledTooltip={emphasisOrCodeTooltip}
        />
        <UnderlineButton
          onClick={onUnderline}
          active={isUnderline}
          disabled={emphasisDisabled}
          disabledTooltip={emphasisDisabledTooltip}
        />
        <Toolbar.Button
          tooltip={
            nestedMarksBlocked
              ? nestedMarksBlockedTooltip
              : intl.formatMessage({
                  defaultMessage: 'Strikethrough',
                  id: 'lH8y+XQpfx',
                })
          }
          onClick={onStrike}
          active={isStrike}
          disabled={nestedMarksBlocked}
          type="button"
          data-testid="text-menu-strike"
        >
          <Strikethrough size={14} />
        </Toolbar.Button>
        <Toolbar.Button
          tooltip={intl.formatMessage({
            defaultMessage: 'Inline code',
            id: 'fGrFXIEXql',
          })}
          onClick={onInlineCode}
          active={isCode}
          type="button"
          data-testid="text-menu-code"
        >
          <Code size={14} />
        </Toolbar.Button>
        {!isHeading && listsEnabled && (
          <>
            <BulletListButton onClick={onBulletList} active={isBulletList} />
            <OrderedListButton onClick={onOrderedList} active={isOrderedList} />
          </>
        )}
        <Popover
          open={linkEditorOpen}
          onOpenChange={(open) => {
            if (open && nestedMarksBlocked) {
              notifyNestedMarksBlocked();
              return;
            }
            setLinkEditorOpen(open);
          }}
        >
          {/* `Toolbar.Button` renders its tooltip as the outer element, so the
            popover trigger anchors to a wrapping span (a real DOM node) rather
            than the tooltip provider. */}
          <PopoverTrigger asChild>
            <span className="inline-flex">
              <Toolbar.Button
                tooltip={
                  nestedMarksBlocked
                    ? nestedMarksBlockedTooltip
                    : intl.formatMessage({
                        defaultMessage: 'Add link',
                        id: 'JnxkKMhrqm',
                      })
                }
                tooltipShortcuts={
                  nestedMarksBlocked
                    ? undefined
                    : [
                        { key: isMac ? Key.Meta : Key.Control, type: 'modifier' },
                        { key: 'K', type: 'letter' },
                      ]
                }
                active={isLink}
                disabled={nestedMarksBlocked}
                type="button"
                data-testid="text-menu-link"
              >
                <Link size={14} />
              </Toolbar.Button>
            </span>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-2">
            {linkEditorOpen && (
              <LinkEditorPopover
                editor={editor}
                initialHref={(editor.getAttributes('link').href as string) ?? ''}
                hasLink={isLink}
                onClose={() => setLinkEditorOpen(false)}
              />
            )}
          </PopoverContent>
        </Popover>
        {sectionsEnabled && canConvertToSection && (
          <ConvertToSectionButton onClick={onConvertToSection} />
        )}
        <CopyButton onClick={onCopy} data-testid="text-menu-copy" />
        {enableFindReplace && (
          <FindReplaceButton
            onClick={onFindReplace}
            editor={editor}
            data-testid="text-menu-find-replace"
          />
        )}
        {enableSnippet && (
          <SnippetButton
            onClick={onCreateSnippet}
            editor={editor}
            data-testid="text-menu-snippet"
          />
        )}
      </TextMenuWrapper>
    </EditorAnalyticsProvider>
  );
};
